class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();

    this.header = document.querySelector(".section-header");
    this.annoucementBar = document.querySelector(".announcement-bar-section");
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector)").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 989px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        // When motion isn't reduced, addTrapFocus() runs on "transitionend"
        // (well after these writes settle, so it's not forced). With
        // reduced motion there's no transition to wait for, so it would
        // otherwise call trapFocus()'s .focus() right after the classList
        // writes above - deferred a frame to avoid forcing layout.
        !reducedMotion || reducedMotion.matches ? requestAnimationFrame(addTrapFocus) : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);

    if (this.header) {
      this.header.classList.add("reset-z-index");
    }
    if (this.annoucementBar) {
      this.annoucementBar.classList.add("reset-z-index");
    }

    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // Deferred to the next frame - trapFocus() calls .focus(), which needs
    // up-to-date layout to find focusable elements, so calling it right
    // after the classList writes above forces a synchronous layout.
    requestAnimationFrame(() => trapFocus(this.mainDetailsToggle, summaryElement));
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details:not(.mobile-facets__details)").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove(`overflow-hidden-undefined`);
    // Deferred for the same reason as openMenuDrawer - removeTrapFocus()
    // calls .focus(), which would otherwise force a synchronous layout
    // right after the classList writes above.
    requestAnimationFrame(() => removeTrapFocus(elementToFocus));
    this.closeAnimation(this.mainDetailsToggle);

    setTimeout(() => {
      if (this.header) {
        this.header.classList.remove("reset-z-index");
      }
      if (this.annoucementBar) {
        this.annoucementBar.classList.remove("reset-z-index");
      }
    }, 400);
  }

  onFocusOut() {
    setTimeout(() => {
      // mainDetailsToggle can be null: this class is also used (as
      // mobile-menu-nav/HeaderDrawer) on markup with no <details> element at
      // all, where open/close is handled separately by the <mobile-menu>
      // custom element in ua_mobilemenu.liquid.
      if (this.mainDetailsToggle?.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    // Deferred - same reason as openMenuDrawer/closeMenuDrawer.
    requestAnimationFrame(() => removeTrapFocus(detailsElement.querySelector("summary")));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        const parentOpenDetails = detailsElement.closest("details[open]");
        if (parentOpenDetails) {
          // Deferred a frame - trapFocus()'s .focus() call would otherwise
          // force a synchronous layout right after removeAttribute("open")
          // above, which collapses the details content.
          requestAnimationFrame(() => trapFocus(parentOpenDetails, detailsElement.querySelector("summary")));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }
  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("mobile-menu-nav", HeaderDrawer);

// Coalesces a high-frequency handler (e.g. native "scroll") to run at most
// once per animation frame, using the most recent call's arguments, so
// layout-reading handlers don't force a recalculation on every raw event.
function rafThrottle(fn) {
  let scheduled = false;
  let lastArgs;
  return function (...args) {
    lastArgs = args;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      fn.apply(this, lastArgs);
    });
  };
}

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    if (this.classList.contains("slideshow")) {
      this.slider = this;
    } else {
      this.slider = this.querySelector('[id^="Slider-"]');
    }

    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.sliderItemsToShow = [];
    this.ariaNow = this.querySelector("[aria-valuenow]");
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progressBar = this.querySelector(".progress-bar");

    if (!this.slider) return;

    // Deferred to the next frame - this component is frequently constructed
    // synchronously as part of a large innerHTML insert (product
    // recommendations, recently-viewed, shop-the-look), so measuring
    // immediately would force a layout of that whole freshly-inserted
    // subtree instead of letting the browser's own render pass supply it.
    requestAnimationFrame(() => this.initPages());
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", rafThrottle(this.update.bind(this)), { passive: true });
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);

    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
    this.initProgressBar();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    // Deferred to the next frame - callers use resetPages() right after
    // mutating the slider's DOM (e.g. reordering slides), so measuring
    // synchronously here would force a layout right after that write.
    // initPages() isn't otherwise time-critical, so a frame's delay is fine.
    requestAnimationFrame(() => this.initPages());
  }

  initProgressBar() {
    if (!this.progressBar || this.totalPages <= 1) return;

    const barWidthPercentage = (1 / this.totalPages) * 100;

    this.progressBar.style.width = `${barWidthPercentage}%`;
  }

  // Read-only: returns the progress bar's target offset without writing
  // anything, so callers can batch this with other reads before any writes.
  getProgressBarOffset() {
    if (!this.progressBar || this.totalPages <= 1) return null;

    const containerWidth = this.progressBar.parentNode.getBoundingClientRect().width;
    const progressBarWidth = this.progressBar.getBoundingClientRect().width;
    const maxMovableWidth = containerWidth - progressBarWidth;
    const progressPercentage = (this.currentPage - 1) / (this.totalPages - 1);

    return maxMovableWidth * progressPercentage;
  }

  updateAriaNow() {
    if (this.ariaNow) {
      this.ariaNow.setAttribute("aria-valuenow", this.currentPage);
    }
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider) return;

    // Read everything layout-dependent first, before any writes below -
    // otherwise each write forces the next read to trigger a synchronous
    // layout recalculation (this runs on every scroll animation frame).
    const previousPage = this.currentPage;
    const scrollLeft = this.slider.scrollLeft;
    this.currentPage = Math.round(scrollLeft / this.sliderItemOffset) + 1;

    const progressBarOffset = this.getProgressBarOffset();
    const isFirstSlideVisible = this.enableSliderLooping ? null : this.isSlideVisible(this.sliderItemsToShow[0], 0, scrollLeft);
    const isLastSlideVisible = this.enableSliderLooping ? null : this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], 0, scrollLeft);

    // All writes happen after the reads above.
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (progressBarOffset !== null) {
      this.progressBar.style.transform = `translateX(${progressBarOffset}px)`;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (isFirstSlideVisible) {
      this.prevButton?.setAttribute("disabled", "disabled");
    } else {
      this.prevButton?.removeAttribute("disabled");
    }

    if (isLastSlideVisible) {
      this.nextButton?.setAttribute("disabled", "disabled");
    } else {
      this.nextButton?.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0, scrollLeft = this.slider.scrollLeft) {
    const lastVisibleSlide = this.slider.clientWidth + scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === "next" ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  connectedCallback() {
    // Fix a bug where carousels are sometimes scrolled to the far left on page load
    if (this.dataset.reset_scroll == "true") {
      // connectedCallback() runs synchronously during custom element
      // upgrade, early in page load - a single requestAnimationFrame isn't
      // a strong enough defer here, since third-party scripts (Trustpilot,
      // search, marketing/A-B-test tags) keep scheduling style
      // recalculations continuously through most of that window, so "the
      // next frame" often still has something pending. requestIdleCallback
      // waits until the browser is genuinely idle instead of just one frame
      // later, which reliably lands after that settles (Safari has no
      // requestIdleCallback, so it falls back to the rAF defer there).
      const resetScroll = () => {
        this.slider.scrollLeft = 0;
      };
      if ("requestIdleCallback" in window) {
        requestIdleCallback(resetScroll);
      } else {
        requestAnimationFrame(resetScroll);
      }
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.querySelector(".slideshow__slide");
    // sliderItemsToShow isn't populated yet (initPages() is deferred by the
    // parent constructor), so this can no longer be conditional on its
    // length - update() will recompute the real value once that runs.
    this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", rafThrottle(this.setSlideVisibility.bind(this)), { passive: true });
    // Deferred so this runs after the parent's deferred initPages() has
    // populated sliderItemsToShow - rAF callbacks run in registration order
    // within the same frame, and the parent's was registered first (in
    // super()), so ordering is guaranteed.
    requestAnimationFrame(() => this.setSlideVisibility());

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton?.addEventListener("click", this.autoPlayToggle.bind(this));
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
    const isWrapPrevious = isFirstSlide && event.currentTarget.name === "previous";
    const isWrapNext = isLastSlide && event.currentTarget.name === "next";

    if (!isWrapPrevious && !isWrapNext) {
      super.onButtonClick(event);
      return;
    }

    // Looping past either end: super.onButtonClick() would scrollTo() a
    // negative/overflowing position (clamped by the browser), and reading
    // scrollLeft back afterwards to correct it would force a synchronous
    // layout right after that write. The clamped position is always the
    // start (previous, wrapping to the end) or the browser clamps forward
    // overscroll to the end (next, wrapping to the start) - so the wrap
    // target can be computed up front from geometry read before any write,
    // and applied with a single scrollTo() instead of two.
    event.preventDefault();
    this.slideScrollPosition = isWrapPrevious ? this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length : 0;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton?.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton?.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton?.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton?.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton?.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector(".slideshow__slide").clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);
